/**
 * 摄像头管理器
 * 统一管理摄像头的开启、关闭和状态监控
 */
class CameraManager {
  constructor() {
    this.activeStreams = new Set()
    this.isInitialized = false
  }

  /**
   * 初始化摄像头管理器
   */
  init() {
    if (this.isInitialized) return
    this.isInitialized = true
  }

  /**
   * 获取摄像头权限并创建媒体流
   * @param {Object} options 媒体选项
   * @returns {Promise<MediaStream>} 媒体流
   */
  async requestCameraAccess(options = { video: true, audio: true }) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(options)
      this.activeStreams.add(stream)
      return stream
    } catch (error) {
      console.error('获取摄像头权限失败:', error)
      throw error
    }
  }

  /**
   * 停止指定的媒体流
   * @param {MediaStream} stream 要停止的媒体流
   */
  stopStream(stream) {
    if (stream && stream.getTracks) {
      stream.getTracks().forEach(track => {
        track.stop()
      })
      this.activeStreams.delete(stream)
    }
  }

  /**
   * 停止所有活跃的媒体流
   */
  stopAllStreams() {
    this.activeStreams.forEach(stream => {
      this.stopStream(stream)
    })
    this.activeStreams.clear()
  }

  /**
   * 强制关闭所有摄像头和麦克风
   */
  forceCloseAllDevices() {
    try {
      
      // 停止所有已知的媒体流
      this.stopAllStreams()

      // 尝试获取并停止所有可能的媒体流
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        navigator.mediaDevices.getUserMedia({ video: true, audio: true })
          .then(stream => {
            this.stopStream(stream)
          })
          .catch(err => {
            console.log('没有额外的媒体流需要停止')
          })
      }

      // 停止所有视频和音频元素
      this.stopAllMediaElements()

      // 通知浏览器释放设备
      if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        navigator.mediaDevices.enumerateDevices()
          .then(devices => {})
      }

    } catch (error) {
      console.error('强制关闭设备失败:', error)
    }
  }

  /**
   * 停止所有媒体元素
   */
  stopAllMediaElements() {
    // 停止所有视频元素
    const videoElements = document.querySelectorAll('video')
    videoElements.forEach(video => {
      if (video.srcObject) {
        const stream = video.srcObject
        if (stream && typeof stream === 'object' && 'getTracks' in stream) {
          stream.getTracks().forEach(track => {
            track.stop()
          })
        }
        video.srcObject = null
      }
    })

    // 停止所有音频元素
    const audioElements = document.querySelectorAll('audio')
    audioElements.forEach(audio => {
      if (audio.srcObject) {
        const stream = audio.srcObject
        if (stream && typeof stream === 'object' && 'getTracks' in stream) {
          stream.getTracks().forEach(track => {
            track.stop()
          })
        }
        audio.srcObject = null
      }
    })
  }

  /**
   * 检查是否有活跃的媒体流
   * @returns {boolean} 是否有活跃的媒体流
   */
  hasActiveStreams() {
    return this.activeStreams.size > 0
  }

  /**
   * 获取活跃媒体流数量
   * @returns {number} 活跃媒体流数量
   */
  getActiveStreamCount() {
    return this.activeStreams.size
  }

  /**
   * 销毁摄像头管理器
   */
  destroy() {
    this.forceCloseAllDevices()
    this.isInitialized = false
  }
}

// 创建单例实例
const cameraManager = new CameraManager()

export default cameraManager

